import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
    Row, Col, Card, CardBody, Input, Spinner, Alert, Button,
    Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { API } from "../../api/urls";
import { fetchSupportBadge } from "../../store/supportBadge/actions";
import StatCards, { STAT_ACCENTS } from "../../components/Common/StatCards";
import PaginationBar from "../../components/Common/PaginationBar";
import NewTicketModal from "../Support/NewTicketModal";
import { ticketListStyles, TICKET_NAVY } from "../Support/ticketListStyles";
import {
    StatusPill, PriorityPill, CATEGORY_META, CATEGORY_OPTIONS, PRIORITY_META, PRIORITY_OPTIONS,
    STATUS_OPTIONS, STATUS_META, fmtDateTime, formatTicketNo,
} from "../Support/ticketMeta";

const NAVY = TICKET_NAVY;

// Atanan filtresinde "hiç atanmamış" seçeneği. UUID olmadığı için backend'e ayrı bir
// parametre (unassignedOnly) olarak gider; aynı açılır listede durduğu için sentinel.
const UNASSIGNED = "UNASSIGNED";

/** Satır sonundaki işlemler menüsü: detay, atama ve durum değişimi listeden çıkmadan. */
const RowActions = ({ ticket, assignees, busy, onAssign, onStatus, onOpen }) => {
    const [open, setOpen] = useState(false);
    return (
        <Dropdown isOpen={open} toggle={() => setOpen((o) => !o)} direction="start">
            <DropdownToggle tag="button" className="btn btn-link text-muted p-0 border-0"
                onClick={(e) => e.stopPropagation()} aria-label="İşlemler">
                <i className="bx bx-dots-vertical-rounded fs-5" />
            </DropdownToggle>
            {/* Menüdeki tıklamalar satırın kendi onClick'ini (detaya git) tetiklemesin. */}
            <DropdownMenu end onClick={(e) => e.stopPropagation()}>
                <DropdownItem onClick={onOpen}>
                    <i className="bx bx-detail me-2" />Detaya git
                </DropdownItem>

                <DropdownItem divider />
                <DropdownItem header>Ata</DropdownItem>
                {assignees.map((a) => (
                    <DropdownItem key={a.id} disabled={busy || a.id === ticket.assignedToUserId}
                        onClick={() => onAssign(a.id)}>
                        <i className="bx bx-user-check me-2" />{a.username}
                    </DropdownItem>
                ))}
                <DropdownItem disabled={busy || !ticket.assignedToUserId} onClick={() => onAssign(null)}>
                    <i className="bx bx-user-x me-2" />Atamayı kaldır
                </DropdownItem>

                <DropdownItem divider />
                <DropdownItem header>Durum</DropdownItem>
                {STATUS_OPTIONS.map((s) => (
                    <DropdownItem key={s} disabled={busy || s === ticket.status} onClick={() => onStatus(s)}>
                        <i className="bx bx-radio-circle-marked me-2" style={{ color: STATUS_META[s].color }} />
                        {STATUS_META[s].label}
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </Dropdown>
    );
};

export default function AdminTicketsPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Özet sayaçlar menü rozetiyle aynı store alanından okunur: tek uç (admin summary),
    // tek istek, rozetle kartlar hep aynı anı gösterir.
    const summary = useSelector((state) => state.supportBadge);

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const [rowBusy, setRowBusy] = useState(null);

    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [assigneeFilter, setAssigneeFilter] = useState("");
    const [sortBy, setSortBy] = useState("lastReplyAt");
    const [sortDir, setSortDir] = useState("desc");

    const [assignees, setAssignees] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);

    // Kullanıcı Detay → "Tümünü gör" → /admin/tickets?createdByUserId=<uuid>.
    // Ekranda seçilebilir bir filtre değil; yalnızca URL'den gelir ve isteğe eklenir.
    const [searchParams, setSearchParams] = useSearchParams();
    const createdByUserId = searchParams.get("createdByUserId") || "";

    // Listeye en son hangi arama teriminin gönderildiği. Debounce efekti bununla
    // karşılaştırır: ilk açılışta ve filtre temizlemede aynı arama iki kez istek atmaz.
    // (Sadece "ilk render mi" bayrağı yetmiyordu: arama zaten boşken temizlemek bayrağı
    // açık bırakıp kullanıcının BİR SONRAKİ aramasını yutuyordu.)
    const appliedSearch = useRef("");
    document.title = "Destek Yönetimi | Tahsilat360";

    const fetchTickets = useCallback(async (p = 0, opts = {}) => {
        const size = opts.size ?? pageSize;
        const search = opts.search ?? searchTerm;
        const status = opts.status ?? statusFilter;
        const priority = opts.priority ?? priorityFilter;
        const category = opts.category ?? categoryFilter;
        const assignee = opts.assignee ?? assigneeFilter;
        const sBy = opts.sortBy ?? sortBy;
        const sDir = opts.sortDir ?? sortDir;
        try {
            setLoading(true); setError(null);
            const params = { page: p, size };
            if (search) params.searchTerm = search;
            if (status) params.status = status;
            if (priority) params.priority = priority;
            if (category) params.category = category;
            if (assignee === UNASSIGNED) params.unassignedOnly = true;
            else if (assignee) params.assignedToUserId = assignee;
            if (sBy) { params.sortBy = sBy; params.sortDir = sDir; }
            if (createdByUserId) params.createdByUserId = createdByUserId;
            const { data } = await axios.get(API.admin_tickets_list, { params });
            appliedSearch.current = search;
            const d = data.data;
            setTickets(d.content || []);
            setTotalPages(d.totalPages || 0);
            setTotalElements(d.totalElements || 0);
            setPage(d.page ?? p);
            // Kartlar ve menü rozeti listeyle aynı anda tazelensin; ayrı bir polling yok.
            dispatch(fetchSupportBadge());
        } catch (err) {
            if (err.response?.status === 401) return navigate("/login");
            if (err.response?.status === 403) { setForbidden(true); return; }
            setError(err.response?.data?.message || "Talepler yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, [pageSize, searchTerm, statusFilter, priorityFilter, categoryFilter, assigneeFilter,
        sortBy, sortDir, createdByUserId, navigate, dispatch]);

    useEffect(() => { fetchTickets(0); /* eslint-disable-next-line */ }, []);

    // Atanan filtresi ve satır menüsü aynı personel listesini kullanır; bir kez yüklenir.
    useEffect(() => {
        axios.get(API.admin_ticket_assignees)
            .then(({ data }) => setAssignees(data.data || []))
            .catch(() => setAssignees([]));
    }, []);

    // Arama debounce
    useEffect(() => {
        if (searchTerm === appliedSearch.current) return;
        const t = setTimeout(() => fetchTickets(0, { search: searchTerm }), 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [searchTerm]);

    const handleSort = (field) => {
        const nextDir = sortBy === field ? (sortDir === "asc" ? "desc" : "asc") : "asc";
        setSortBy(field); setSortDir(nextDir);
        fetchTickets(0, { sortBy: field, sortDir: nextDir });
    };
    const sortIcon = (field) =>
        sortBy === field ? (sortDir === "asc" ? "bx bx-sort-up" : "bx bx-sort-down") : "bx bx-sort-alt-2";

    const SortableTh = ({ field, children, className }) => (
        <Th className={`sort-th ${className || ""}`} onClick={() => handleSort(field)}>
            {children} <i className={sortIcon(field)} />
        </Th>
    );

    /** Kaç filtre aktif — "Filtreleri Temizle" yalnız gerçekten filtre varken iş yapar. */
    const activeFilterCount =
        [searchTerm, statusFilter, priorityFilter, categoryFilter, assigneeFilter, createdByUserId]
            .filter(Boolean).length;

    const clearFilters = () => {
        setSearchTerm(""); setStatusFilter(""); setPriorityFilter("");
        setCategoryFilter(""); setAssigneeFilter("");
        // Kullanıcıdan gelen ön filtre de temizlenir, yoksa "temizledim ama liste dar" olurdu.
        if (createdByUserId) setSearchParams({});
        fetchTickets(0, { search: "", status: "", priority: "", category: "", assignee: "" });
    };

    /** Atama/durum değişimi sonrası liste ve sayaçlar tazelenir; sayfa yerinde kalır. */
    const refreshCurrent = () => fetchTickets(page);

    const handleAssign = async (ticketId, assigneeId) => {
        setRowBusy(ticketId);
        try {
            await axios.patch(API.admin_ticket_assign(ticketId), { assigneeId: assigneeId || null });
            toast.success(assigneeId ? "Talep atandı" : "Atama kaldırıldı");
            refreshCurrent();
        } catch (err) {
            toast.error(err.response?.data?.message || "Atama güncellenemedi");
        } finally {
            setRowBusy(null);
        }
    };

    const handleStatus = async (ticketId, status) => {
        setRowBusy(ticketId);
        try {
            await axios.patch(API.admin_ticket_update(ticketId), { status });
            toast.success(`Durum "${STATUS_META[status].label}" olarak güncellendi`);
            refreshCurrent();
        } catch (err) {
            toast.error(err.response?.data?.message || "Durum güncellenemedi");
        } finally {
            setRowBusy(null);
        }
    };

    /** "%70,20 tüm talepler" — toplam sıfırken oran yazılmaz, yanıltıcı olurdu. */
    const ratioHint = (count) => (summary.totalCount > 0
        ? `%${((count / summary.totalCount) * 100).toFixed(2).replace(".", ",")} tüm talepler`
        : "—");

    const summaryItems = useMemo(() => [
        {
            key: "total", label: "Toplam Talep", value: summary.totalCount,
            hint: "Tüm zamanlar", accent: STAT_ACCENTS.navy,
        },
        {
            key: "awaiting", label: "Yanıt Bekleyen", value: summary.awaitingReplyCount,
            hint: ratioHint(summary.awaitingReplyCount), accent: STAT_ACCENTS.amber,
        },
        {
            key: "answered", label: "Yanıtlanan", value: summary.answeredCount,
            hint: ratioHint(summary.answeredCount), accent: STAT_ACCENTS.green,
        },
        {
            key: "closed", label: "Kapalı", value: summary.closedCount,
            hint: ratioHint(summary.closedCount), accent: STAT_ACCENTS.red,
        },
        // eslint-disable-next-line
    ], [summary]);

    if (forbidden) {
        return <div className="page-content"><div className="container-fluid">
            <Alert color="warning">Bu sayfayı görüntüleme yetkiniz yok.</Alert>
        </div></div>;
    }

    return (
        <div className="page-content tickets-modern">
            <style>{ticketListStyles}</style>
            <div className="container-fluid">

                {/* ── Başlık ─────────────────────────────────────────── */}
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                    <div>
                        <div className="mb-1" style={{ fontSize: ".82rem" }}>
                            <Link to="/support" style={{ color: NAVY, fontWeight: 600 }}>Destek</Link>
                            <i className="bx bx-chevron-right mx-1 text-muted" />
                            <span className="text-muted">Destek Yönetimi</span>
                        </div>
                        <h4 className="mb-1 fw-bold text-dark">Destek Yönetimi</h4>
                        <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                            Müşteri taleplerini takip edin ve yönetin.
                        </p>
                    </div>
                    <Button className="d-flex align-items-center gap-1"
                        style={{ background: NAVY, borderColor: NAVY }}
                        onClick={() => setCreateOpen(true)}>
                        <i className="bx bx-plus" /> Talep Oluştur
                    </Button>
                </div>

                {/* ── Özet kartları ──────────────────────────────────── */}
                <StatCards className="mb-3" loading={summary.loading} items={summaryItems} />

                <Row>
                    <Col>
                        <Card className="rounded-4 border-0 shadow-sm">
                            {/* ── Filtreler ──────────────────────────── */}
                            <div className="filter-bar">
                                <Input type="search" bsSize="sm" className="rounded-pill filter-search"
                                    placeholder="Konu, firma, açan veya talep no ara..."
                                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

                                <Input type="select" bsSize="sm" className="rounded-pill filter-select"
                                    value={categoryFilter}
                                    onChange={(e) => { setCategoryFilter(e.target.value); fetchTickets(0, { category: e.target.value }); }}>
                                    <option value="">Tüm Kategoriler</option>
                                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{CATEGORY_META[c]}</option>)}
                                </Input>

                                <Input type="select" bsSize="sm" className="rounded-pill filter-select"
                                    value={priorityFilter}
                                    onChange={(e) => { setPriorityFilter(e.target.value); fetchTickets(0, { priority: e.target.value }); }}>
                                    <option value="">Tüm Öncelikler</option>
                                    {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
                                </Input>

                                <Input type="select" bsSize="sm" className="rounded-pill filter-select"
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); fetchTickets(0, { status: e.target.value }); }}>
                                    <option value="">Tüm Durumlar</option>
                                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                                </Input>

                                <Input type="select" bsSize="sm" className="rounded-pill filter-select"
                                    value={assigneeFilter}
                                    onChange={(e) => { setAssigneeFilter(e.target.value); fetchTickets(0, { assignee: e.target.value }); }}>
                                    <option value="">Tüm Atananlar</option>
                                    <option value={UNASSIGNED}>Atanmamış</option>
                                    {assignees.map((a) => <option key={a.id} value={a.id}>{a.username}</option>)}
                                </Input>

                                <Button size="sm" outline color="secondary" className="rounded-pill ms-auto"
                                    disabled={activeFilterCount === 0} onClick={clearFilters}>
                                    <i className="bx bx-refresh me-1" />Filtreleri Temizle
                                </Button>
                            </div>

                            <CardBody className="pt-3">
                                {error && <Alert color="danger" className="rounded-3">{error}</Alert>}

                                {loading ? (
                                    <div className="text-center p-5"><Spinner color="primary" /></div>
                                ) : (
                                    <>
                                        <div className="table-responsive rounded-4 border">
                                            <Table className="table table-hover mb-0">
                                                <Thead>
                                                    <Tr>
                                                        <SortableTh field="subject">Konu</SortableTh>
                                                        <SortableTh field="company.companyName" className="d-none d-lg-table-cell">Firma</SortableTh>
                                                        <SortableTh field="createdBy.username" className="d-none d-md-table-cell">Açan</SortableTh>
                                                        <SortableTh field="category" className="d-none d-xl-table-cell">Kategori</SortableTh>
                                                        <SortableTh field="priority">Öncelik</SortableTh>
                                                        <SortableTh field="status">Durum</SortableTh>
                                                        <SortableTh field="lastReplyAt" className="d-none d-md-table-cell">Son Yanıt</SortableTh>
                                                        <SortableTh field="createdAt" className="d-none d-lg-table-cell">Oluşturma</SortableTh>
                                                        <Th style={{ width: 48 }}>İşlemler</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {tickets.length ? tickets.map((t) => (
                                                        <Tr key={t.id} style={{ cursor: "pointer" }}
                                                            onClick={() => navigate(`/admin/tickets/${t.id}`)}>
                                                            <Td>
                                                                <div className="ticket-subject">
                                                                    {t.subject}
                                                                    {!t.lastReplyByStaff && (t.status === "OPEN" || t.status === "IN_PROGRESS") && (
                                                                        <span className="ms-2 badge bg-warning-subtle text-warning"
                                                                            style={{ fontSize: ".62rem" }}>yanıt bekliyor</span>
                                                                    )}
                                                                </div>
                                                                <div className="ticket-no">#{formatTicketNo(t.ticketNo)}</div>
                                                            </Td>
                                                            <Td className="d-none d-lg-table-cell">{t.companyName || "—"}</Td>
                                                            <Td className="d-none d-md-table-cell">{t.createdByUsername}</Td>
                                                            <Td className="d-none d-xl-table-cell">{CATEGORY_META[t.category] || t.category}</Td>
                                                            <Td><PriorityPill priority={t.priority} /></Td>
                                                            <Td><StatusPill status={t.status} /></Td>
                                                            <Td className="d-none d-md-table-cell">{fmtDateTime(t.lastReplyAt)}</Td>
                                                            <Td className="d-none d-lg-table-cell">{fmtDateTime(t.createdAt)}</Td>
                                                            <Td onClick={(e) => e.stopPropagation()}>
                                                                {rowBusy === t.id
                                                                    ? <Spinner size="sm" color="secondary" />
                                                                    : <RowActions
                                                                        ticket={t}
                                                                        assignees={assignees}
                                                                        busy={rowBusy === t.id}
                                                                        onOpen={() => navigate(`/admin/tickets/${t.id}`)}
                                                                        onAssign={(id) => handleAssign(t.id, id)}
                                                                        onStatus={(s) => handleStatus(t.id, s)}
                                                                    />}
                                                            </Td>
                                                        </Tr>
                                                    )) : (
                                                        <Tr>
                                                            <Td colSpan="9" className="text-center py-4 text-muted">
                                                                {activeFilterCount > 0
                                                                    ? "Filtrelere uyan talep bulunamadı"
                                                                    : "Talep bulunamadı"}
                                                            </Td>
                                                        </Tr>
                                                    )}
                                                </Tbody>
                                            </Table>
                                        </div>

                                        <PaginationBar
                                            currentPage={page}
                                            totalPages={totalPages}
                                            totalElements={totalElements}
                                            pageSize={pageSize}
                                            onPageChange={(p) => fetchTickets(p)}
                                            onPageSizeChange={(s) => { setPageSize(s); fetchTickets(0, { size: s }); }}
                                        />
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            <NewTicketModal
                isOpen={createOpen}
                toggle={() => setCreateOpen(false)}
                onCreated={(newId) => (newId ? navigate(`/admin/tickets/${newId}`) : fetchTickets(0))}
            />
        </div>
    );
}
